import { motion } from "framer-motion";
import { ArrowRight, Boxes, Smartphone, Sparkles, Globe2, Wrench } from "lucide-react";
import { Link } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";

/* Cards mirror the primary commercial services. Each opens the estimate wizard
 * pre-scoped to that service AND deep-links to a dedicated service landing page
 * (created in Phase 2). */
const CARDS = [
  {
    key: "crm",
    icon: Boxes,
    label: "Custom CRM",
    copy: "Sales, operations, workflow automation and business management platforms.",
    cta: "Explore CRM Development",
    href: "/services/crm-development",
    accent: "from-sky-500/25 via-sky-500/10 to-transparent",
  },
  {
    key: "mobile",
    icon: Smartphone,
    label: "Mobile Application",
    copy: "iOS, Android and cross-platform applications designed for real business use.",
    cta: "Explore App Development",
    href: "/services/mobile-app-development",
    accent: "from-violet-500/25 via-violet-500/10 to-transparent",
  },
  {
    key: "ai",
    icon: Sparkles,
    label: "AI & Automation",
    copy: "AI agents, intelligent workflows, document processing and business automation.",
    cta: "Explore AI Development",
    href: "/services/ai-development",
    accent: "from-fuchsia-500/25 via-fuchsia-500/10 to-transparent",
  },
  {
    key: "web",
    icon: Globe2,
    label: "Custom Web Application",
    copy: "Secure and scalable platforms designed around your organisation.",
    cta: "Explore Web App Development",
    href: "/services/web-application-development",
    accent: "from-emerald-500/25 via-emerald-500/10 to-transparent",
  },
  {
    key: "upgrade",
    icon: Wrench,
    label: "Existing System Upgrade",
    copy: "Modernisation, integrations, cloud migration and new feature development.",
    cta: "Discuss Your System",
    href: "/services/custom-software-development",
    accent: "from-amber-500/25 via-amber-500/10 to-transparent",
  },
];

export default function WhatAreYouBuilding() {
  const openWizard = (key, source) => {
    // NB: don't push project_estimate_started here — the global wizard fires it once on open.
    window.dispatchEvent(new CustomEvent("open-estimate-wizard", { detail: { presetType: key, source } }));
  };

  return (
    <section className="relative py-20 md:py-28 border-t border-border" data-testid="what-building-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">Start here</div>
            <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl md:text-5xl leading-[1.1] max-w-3xl">
              What are you looking to build?
            </h2>
            <p className="mt-3 text-base text-muted-foreground max-w-2xl">
              Pick the closest fit — we'll help you refine scope, cost and timeline in a free 24-hour assessment.
            </p>
          </div>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("open-estimate-wizard", { detail: { source: "what_building_top" } }))}
            className="self-start md:self-auto rounded-full px-5 py-2.5 bg-primary text-primary-foreground text-sm font-semibold inline-flex items-center gap-2"
            data-testid="what-building-cta-generic"
          >
            Not sure? Get a Free Assessment <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CARDS.map((c, i) => (
            <motion.div
              key={c.key}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="group relative rounded-3xl border border-border bg-card overflow-hidden hover:border-primary/40 transition-colors"
              data-testid={`what-building-card-${c.key}`}
            >
              <div className={`absolute inset-x-0 -top-24 h-56 bg-gradient-to-b ${c.accent} pointer-events-none blur-2xl opacity-70`} aria-hidden />
              <div className="relative p-6 md:p-7 flex flex-col h-full min-h-[260px]">
                <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary grid place-items-center">
                  <c.icon className="w-5 h-5" />
                </div>
                <h3 className="mt-5 font-heading font-bold text-xl leading-tight">{c.label}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.copy}</p>
                <div className="mt-auto pt-5 flex items-center gap-3 flex-wrap">
                  <Link
                    to={c.href}
                    onClick={() => trackEvent("service_link_clicked", { service: c.key, source: "what_building" })}
                    className="text-sm font-medium text-primary inline-flex items-center gap-1.5 hover:underline"
                    data-testid={`what-building-card-cta-${c.key}`}
                  >
                    {c.cta} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                  <button
                    onClick={() => openWizard(c.key, `what_building_${c.key}`)}
                    className="ml-auto text-[11px] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
                    data-testid={`what-building-card-estimate-${c.key}`}
                  >
                    Get estimate →
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
